import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Users, DollarSign, Home } from 'lucide-react';
import { API_BASE_URL } from '../../config/api.config';

interface Booking {
  id: number;
  bookingNumber: string;
  schedule: {
    date: string;
    time: string;
    tourTypeName: string;
  };
  numberOfParticipants: number;
  totalPrice: number;
  paymentAmount: number;
  paymentMethod: string;
  customerName: string;
  customerEmail: string;
}

const BookingConfirmation: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBooking();
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/bookings/${bookingId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBooking(data.data || data);
      }
    } catch (error) {
      console.error('Error loading booking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800">Buchung nicht gefunden.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Zurück zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Success Header */}
      <div className="text-center mb-8 animate-fade-in">
        <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Buchung erfolgreich!
        </h1>
        <p className="text-gray-600">
          Ihre Buchung wurde erfolgreich erstellt. Eine Bestätigung wurde an {booking.customerEmail} gesendet.
        </p>
      </div>

      {/* Booking Number */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6 text-center">
        <p className="text-sm text-blue-800 mb-1">Ihre Buchungsnummer</p>
        <p className="text-3xl font-bold text-blue-900">{booking.bookingNumber}</p>
      </div>

      {/* Booking Details */}
      <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-600" />
            Termindetails
          </h2>
          <div className="space-y-2 text-gray-700">
            <p><strong>Tour:</strong> {booking.schedule.tourTypeName}</p>
            <p><strong>Datum:</strong> {booking.schedule.date}</p>
            <p><strong>Uhrzeit:</strong> {booking.schedule.time}</p>
          </div>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-600" />
            Teilnehmer
          </h2>
          <p className="text-gray-700">
            <strong>{booking.numberOfParticipants}</strong> Personen
          </p>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <DollarSign className="w-6 h-6 mr-2 text-blue-600" />
            Zahlungsdetails
          </h2>
          <div className="space-y-2 text-gray-700">
            <p><strong>Gesamtpreis:</strong> {booking.totalPrice.toFixed(2)} €</p>
            <p><strong>Bezahlt:</strong> {booking.paymentAmount.toFixed(2)} €</p>
            <p><strong>Zahlungsart:</strong> {booking.paymentMethod === 'Deposit' ? 'Anzahlung (20%)' : 'Vollzahlung'}</p>
            {booking.paymentMethod === 'Deposit' && (
              <p className="text-sm text-gray-600">
                Restbetrag ({(booking.totalPrice - booking.paymentAmount).toFixed(2)} €)
                bitte vor Ort zahlen
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8 flex gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold flex items-center justify-center"
        >
          <Home className="w-5 h-5 mr-2" />
          Zum Dashboard
        </button>
        <button
          onClick={() => navigate('/booking')}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Neue Buchung
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmation;
